(() => {
    const FRAME_ID = 'BIS_Frame';
    const STYLE_ID = 'bis-modal-scroll-fix';
    const HOST_LOCK_ATTR = 'data-bis-host-scroll-lock';
    let watchdogId = null;
    const STYLE_TEXT = `
      html,
      body {
        height: 100% !important;
        overflow: hidden !important;
        padding-right: 0 !important;
        scrollbar-gutter: auto !important;
        overscroll-behavior: none !important;
      }

      body::-webkit-scrollbar,
      html::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
        display: none !important;
      }

      *::-webkit-scrollbar {
        width: 0 !important;
        height: 0 !important;
      }

      .modal {
        overflow: hidden !important;
      }

      #container {
        max-height: calc(100dvh - 2rem) !important;
        overflow-y: auto !important;
        overscroll-behavior: contain;
      }
    `;

    const getFrameDocument = (frame) => {
        try {
            return frame.contentDocument || frame.contentWindow?.document || null;
        } catch (error) {
            return null;
        }
    };

    const setHostScrollLock = (locked) => {
        const html = document.documentElement;
        const body = document.body;
        if (!html || !body) return;

        if (locked) {
            if (body.hasAttribute(HOST_LOCK_ATTR)) return;

            body.setAttribute(HOST_LOCK_ATTR, 'true');
            body.dataset.bisHostPrevBodyOverflow = body.style.overflow || '';
            body.dataset.bisHostPrevHtmlOverflow = html.style.overflow || '';
            body.style.setProperty('overflow', 'hidden', 'important');
            html.style.setProperty('overflow', 'hidden', 'important');
            return;
        }

        if (!body.hasAttribute(HOST_LOCK_ATTR)) return;
        body.removeAttribute(HOST_LOCK_ATTR);
        body.style.overflow = body.dataset.bisHostPrevBodyOverflow || '';
        html.style.overflow = body.dataset.bisHostPrevHtmlOverflow || '';
        delete body.dataset.bisHostPrevBodyOverflow;
        delete body.dataset.bisHostPrevHtmlOverflow;
    };

    const isBisModalOpen = (doc) => {
        const body = doc?.body;
        if (!body) return false;
        if (body.classList.contains('fadein') || body.classList.contains('bis-popover-active')) return true;

        const modal = doc.querySelector('.modal.d-block');
        return Boolean(modal);
    };

    const bindFrameStateObserver = (frame, doc) => {
        if (!frame || !doc?.body || frame.dataset.bisStateObserverBound) return;
        frame.dataset.bisStateObserverBound = 'true';

        const syncState = () => {
            setHostScrollLock(isBisModalOpen(doc));
        };

        syncState();

        const observer = new MutationObserver(syncState);
        observer.observe(doc.body, { attributes: true, subtree: true, childList: true, attributeFilter: ['class'] });
    };

    const injectStyle = (frame) => {
        if (!frame) return false;
        const doc = getFrameDocument(frame);
        if (!doc?.head || !doc?.body || !doc?.documentElement) return false;

        if (!doc.getElementById(STYLE_ID)) {
            const style = doc.createElement('style');
            style.id = STYLE_ID;
            style.textContent = STYLE_TEXT;
            doc.head.appendChild(style);
        }

        doc.documentElement.style.setProperty('overflow', 'hidden', 'important');
        doc.documentElement.style.setProperty('height', '100%', 'important');
        doc.documentElement.style.setProperty('padding-right', '0', 'important');
        doc.body.style.setProperty('overflow', 'hidden', 'important');
        doc.body.style.setProperty('overflow-y', 'hidden', 'important');
        doc.body.style.setProperty('height', '100%', 'important');
        doc.body.style.setProperty('padding-right', '0', 'important');

        bindFrameStateObserver(frame, doc);

        frame.setAttribute('scrolling', 'no');
        frame.style.width = '100%';
        frame.style.height = '100%';
        frame.style.overflow = 'hidden';
        return true;
    };

    const startWatchdog = () => {
        if (watchdogId) return;

        watchdogId = window.setInterval(() => {
            const frame = document.getElementById(FRAME_ID);
            if (!frame) {
                setHostScrollLock(false);
                return;
            }

            const doc = getFrameDocument(frame);
            if (!doc) return;

            injectStyle(frame);
            setHostScrollLock(isBisModalOpen(doc));
        }, 180);
    };

    const scheduleReapply = (durationMs = 3000, stepMs = 120) => {
        const startedAt = Date.now();

        const tick = () => {
            applyFix();
            if (Date.now() - startedAt < durationMs) {
                window.setTimeout(tick, stepMs);
            }
        };

        tick();
    };

    const applyFix = () => {
        const frame = document.getElementById(FRAME_ID);
        if (!frame) {
            setHostScrollLock(false);
            return;
        }

        if (!frame.dataset.bisFixBound) {
            frame.dataset.bisFixBound = 'true';
            frame.addEventListener('load', () => {
                injectStyle(frame);
                window.setTimeout(() => injectStyle(frame), 150);
                scheduleReapply(2500, 120);
            });
        }

        if (!injectStyle(frame)) {
            window.setTimeout(() => injectStyle(frame), 150);
        }
    };

    const bindOpenTrigger = () => {
        document.addEventListener(
            'click',
            (event) => {
                const target = event.target;
                if (!(target instanceof Element)) return;

                const trigger = target.closest('#BIS_trigger, .notify-button, [id^="BIS_trigger"]');
                if (!trigger) return;

                window.setTimeout(applyFix, 50);
                window.setTimeout(applyFix, 250);
                window.setTimeout(() => scheduleReapply(3000, 120), 30);
            },
            true
        );
    };

    const observeForFrame = () => {
        const root = document.body || document.documentElement;
        if (!root) return;

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (!mutation.addedNodes?.length) continue;

                for (const node of mutation.addedNodes) {
                    if (!(node instanceof Element)) continue;
                    if (node.id === FRAME_ID || node.querySelector?.(`#${FRAME_ID}`)) {
                        applyFix();
                        return;
                    }
                }
            }

            const frame = document.getElementById(FRAME_ID);
            if (!frame) {
                setHostScrollLock(false);
            }
        });

        observer.observe(root, { childList: true, subtree: true });
    };

    const init = () => {
        // Fallback: if AMP ever applies the class on host body, lock host scroll too.
        const hostStyle = document.createElement('style');
        hostStyle.textContent = `
          html.bis-popover-active,
          body.bis-popover-active {
            overflow: hidden !important;
          }

          html:has(#${FRAME_ID}[style*="display: block"]),
          body:has(#${FRAME_ID}[style*="display: block"]) {
            overflow: hidden !important;
          }
        `;
        document.head.appendChild(hostStyle);

        bindOpenTrigger();
        observeForFrame();
        applyFix();
        startWatchdog();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
        init();
    }
})();
